/**
 * PDF Blob Fetcher Utility
 * 
 * Provides a helper function to fetch PDF files as blobs with proper cache control
 * to avoid 304 (Not Modified) responses that don't include a body.
 */

/**
 * Fetch PDF as Blob with proper cache-control headers
 * 
 * This avoids 304 (Not Modified) responses which return no body,
 * causing "Failed to load PDF file" errors in react-pdf.
 * 
 * @param url - The URL to fetch (can be relative or absolute)
 * @param basePath - The base API URL (defaults to current origin)
 * @param getToken - Function to get the auth token
 * @returns Promise<Blob> - The PDF blob
 */
export async function fetchPdfBlob(
  url: string,
  basePath: string = '',
  getToken?: () => string | Promise<string>
): Promise<Blob> {
  const token = getToken ? await getToken() : null;
  
  const fullUrl = url.startsWith('http') ? url : `${basePath}${url}`;

  const response = await fetch(fullUrl, {
    method: 'GET',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      // Force no-cache to avoid 304 responses with empty body
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
    },
    credentials: 'include',
    cache: 'no-store', // Force fetch to not use cache
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  
  // Verify we got actual content
  if (blob.size === 0) {
    throw new Error('Received empty PDF blob (possibly due to caching issue)');
  }

  return blob;
}
